/**
 * Barcode validation and formatting utilities
 * Supports: EAN-13, ISBN-10, ISBN-13, UPC-A
 */

/**
 * Validate EAN-13 barcode using check digit algorithm
 * @param {string} barcode - 13-digit barcode
 * @returns {boolean} - true if valid
 */
export function isValidEAN13(barcode) {
  if (!/^\d{13}$/.test(barcode)) return false;

  const digits = barcode.split('').map(Number);
  const checkDigit = digits.pop();
  
  // Calculate check digit: sum odd positions × 1, even positions × 3
  let sum = 0;
  digits.forEach((digit, index) => {
    sum += digit * (index % 2 === 0 ? 1 : 3);
  });
  
  const calculatedCheckDigit = (10 - (sum % 10)) % 10;
  return calculatedCheckDigit === checkDigit;
}

/**
 * Validate ISBN-10 using check digit algorithm
 * @param {string} isbn - 10-digit ISBN (may include hyphens)
 * @returns {boolean} - true if valid
 */
export function isValidISBN10(isbn) {
  // Remove hyphens and check format
  const cleaned = isbn.replace(/-/g, '');
  if (!/^\d{9}[\dXx]$/.test(cleaned)) return false;

  const digits = cleaned.split('');
  let sum = 0;
  
  for (let i = 0; i < 9; i++) {
    sum += parseInt(digits[i]) * (10 - i);
  }
  
  const checkChar = digits[9];
  const checkDigit = checkChar === 'X' || checkChar === 'x' ? 10 : parseInt(checkChar);
  
  sum += checkDigit;
  return sum % 11 === 0;
}

/**
 * Validate ISBN-13 (same as EAN-13)
 * @param {string} isbn - 13-digit ISBN
 * @returns {boolean} - true if valid
 */
export function isValidISBN13(isbn) {
  const cleaned = isbn.replace(/-/g, '');
  return isValidEAN13(cleaned);
}

/**
 * Validate UPC-A (12-digit barcode)
 * @param {string} barcode - 12-digit UPC-A
 * @returns {boolean} - true if valid
 */
export function isValidUPCA(barcode) {
  if (!/^\d{12}$/.test(barcode)) return false;

  const digits = barcode.split('').map(Number);
  const checkDigit = digits.pop();
  
  // Calculate check digit: sum odd positions × 3, even positions × 1
  let sum = 0;
  digits.forEach((digit, index) => {
    sum += digit * (index % 2 === 0 ? 3 : 1);
  });
  
  const calculatedCheckDigit = (10 - (sum % 10)) % 10;
  return calculatedCheckDigit === checkDigit;
}

/**
 * Detect barcode type and validate
 * @param {string} barcode - Barcode string
 * @returns {object} - {valid: boolean, type: string, formatted: string}
 */
export function validateBarcode(barcode) {
  // Remove spaces and hyphens
  const cleaned = barcode.replace(/[\s-]/g, '');

  // Try EAN-13 / ISBN-13
  if (cleaned.length === 13) {
    if (isValidEAN13(cleaned)) {
      // Check if it's an ISBN (starts with 978 or 979)
      if (cleaned.startsWith('978') || cleaned.startsWith('979')) {
        return { valid: true, type: 'ISBN-13', formatted: cleaned };
      }
      return { valid: true, type: 'EAN-13', formatted: cleaned };
    }
  }

  // Try UPC-A (convert to EAN-13 by prepending 0)
  if (cleaned.length === 12) {
    if (isValidUPCA(cleaned)) {
      return { valid: true, type: 'UPC-A', formatted: '0' + cleaned }; // Store as EAN-13
    }
  }

  // Try ISBN-10
  if (cleaned.length === 10) {
    if (isValidISBN10(cleaned)) {
      // Convert ISBN-10 to ISBN-13
      const isbn13 = convertISBN10toISBN13(cleaned);
      return { valid: true, type: 'ISBN-10', formatted: isbn13 };
    }
  }

  // Invalid barcode
  return { valid: false, type: 'UNKNOWN', formatted: cleaned };
}

/**
 * Convert ISBN-10 to ISBN-13
 * @param {string} isbn10 - 10-digit ISBN
 * @returns {string} - 13-digit ISBN
 */
export function convertISBN10toISBN13(isbn10) {
  const cleaned = isbn10.replace(/-/g, '').slice(0, 9); // Remove check digit
  const isbn13Base = '978' + cleaned;
  
  // Calculate ISBN-13 check digit
  const digits = isbn13Base.split('').map(Number);
  let sum = 0;
  digits.forEach((digit, index) => {
    sum += digit * (index % 2 === 0 ? 1 : 3);
  });
  
  const checkDigit = (10 - (sum % 10)) % 10;
  return isbn13Base + checkDigit;
}

/**
 * Format barcode for display (add hyphens for readability)
 * @param {string} barcode - Raw barcode
 * @param {string} type - Barcode type
 * @returns {string} - Formatted barcode
 */
export function formatBarcodeDisplay(barcode, type) {
  if (type === 'ISBN-13' || type === 'ISBN-10') {
    // Format: 978-0-123-45678-9
    if (barcode.length === 13) {
      return `${barcode.slice(0, 3)}-${barcode.slice(3, 4)}-${barcode.slice(4, 7)}-${barcode.slice(7, 12)}-${barcode.slice(12)}`;
    }
  }
  
  // Default: no formatting
  return barcode;
}

/**
 * Get barcode type label for UI display
 * @param {string} type - Barcode type
 * @returns {string} - Human-readable label
 */
export function getBarcodeTypeLabel(type) {
  const labels = {
    'EAN-13': 'EAN-13 (General Product)',
    'ISBN-13': 'ISBN-13 (Book)',
    'ISBN-10': 'ISBN-10 (Book)',
    'UPC-A': 'UPC-A (US Product)',
    'UNKNOWN': 'Unknown Format'
  };
  return labels[type] || type;
}
